import React, { useState, useEffect } from "react";
import dayjs from "dayjs";
import isBetween from "dayjs/plugin/isBetween";

dayjs.extend(isBetween);

const CalendarGrid = ({ month, selectedRange = {}, onSelectRange }) => {
  // Only set initial month ONCE on mount or when modal opens
  const [displayMonth, setDisplayMonth] = useState(dayjs(month));

  const today = dayjs().startOf("day");

  // When modal re-opens with a new `month`, update displayMonth ONCE.
  useEffect(() => {
    setDisplayMonth(dayjs(month));
  }, [month]);

  // DO NOT sync with selectedRange.from — this caused all jump issues!

  // Swipe
  const [touchStartX, setTouchStartX] = useState(0);
  const handleTouchStart = (e) => setTouchStartX(e.touches[0].clientX);
  const handleTouchEnd = (e) => {
    const diff = touchStartX - e.changedTouches[0].clientX;
    if (diff > 50) setDisplayMonth(displayMonth.add(1, "month"));
    else if (diff < -50 && !displayMonth.isSame(today, "month"))
      setDisplayMonth(displayMonth.subtract(1, "month"));
  };

  // Selection rules
  const canSelectDate = (dateStr, noAvailability) => {
    const date = dayjs(dateStr);

    if (date.isBefore(today, "day")) return false;
    return true;
  };

  const handleDayClick = (dateStr) => {
    if (!canSelectDate(dateStr, false)) return;

    if (selectedRange.from === dateStr && !selectedRange.to) {
      onSelectRange?.({ from: null, to: null });
      return;
    }

    if (!selectedRange.from || (selectedRange.from && selectedRange.to)) {
      onSelectRange?.({ from: dateStr, to: null });
    } else {
      const from = dayjs(selectedRange.from);
      const to = dayjs(dateStr);

      const newRange = to.isBefore(from)
        ? { from: dateStr, to: selectedRange.from }
        : { from: selectedRange.from, to: dateStr };

      onSelectRange?.(newRange);
    }
  };

  // Build calendar cells
  const startOfMonth = displayMonth.startOf("month");
  const endOfMonth = displayMonth.endOf("month");
  const daysInMonth = endOfMonth.date();
  const startDay = startOfMonth.day();

  const calendarCells = [];
  for (let i = 0; i < startDay; i++) calendarCells.push(null);
  for (let day = 1; day <= daysInMonth; day++) {
    const dateStr = startOfMonth.date(day).format("YYYY-MM-DD");
    const date = dayjs(dateStr);
    const noAvailability = false;
    const isPast = date.isBefore(today, "day");
    const isToday = date.isSame(today, "day");

    calendarCells.push({ date: dateStr, noAvailability, isPast, isToday });
  }
  while (calendarCells.length % 7 !== 0) calendarCells.push(null);

  // Styles (unchanged)
  const styles = {
    container: { marginTop: "1rem", fontSize: "0.8rem", width: "100%" },
    header: { display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "0.5rem" },
    navButton: {
      cursor: "pointer",
      border: "none",
      backgroundColor: "#000",
      color: "#fff",
      padding: "6px 12px",
      borderRadius: "6px",
    },
    grid: {
      display: "grid",
      gridTemplateColumns: "repeat(7, minmax(30px, 1fr))",
      gap: "4px",
    },
    cell: { minHeight: "50px" },
    dayCard: {
      cursor: "pointer",
      border: "1px solid #dee2e6",
      backgroundColor: "#fff",
      display: "flex",
      flexDirection: "column",
      justifyContent: "center",
      alignItems: "center",
      fontSize: "0.75rem",
      position: "relative",
      minHeight: "50px",
      padding: "2px",
      color: "#000",
      borderRadius: "6px",
      transition: "0.15s",
    },
    dayCardSelected: { backgroundColor: "#e2dfdfff", color: "#242222ff", border: "2px solid #e2dfdfff" },    
    checkInDate: { backgroundColor: "#000", color: "#fff", border: "2px solid #000" },
    checkOutDate: { backgroundColor: "#000", color: "#fff", border: "2px solid #000" },
    dateLabel: { fontWeight: "bold" },
    noAvailabilitySlash: {
      position: "absolute",
      top: "50%",
      left: "-20%",
      width: "140%",
      height: "2px",
      backgroundColor: "rgba(200,0,0,0.65)",
      transform: "rotate(-45deg)",
      pointerEvents: "none",
    },
    pastDay: { opacity: 0.4, cursor: "not-allowed" },
    todayDay: { border: "1px solid #000", fontWeight: "bold" },
  };

  return (
    <div style={styles.container}>
      {/* Header */}
      <div style={styles.header}>
        <button
          style={styles.navButton}
          onClick={() =>
            !displayMonth.isSame(today, "month") &&
            setDisplayMonth(displayMonth.subtract(1, "month"))
          }
        >
          ‹
        </button>

        <h5 style={{ margin: 0 }}>{displayMonth.format("MMMM YYYY")}</h5>

        <button
          style={styles.navButton}
          onClick={() => setDisplayMonth(displayMonth.add(1, "month"))}
        >
          ›
        </button>
      </div>

      {/* Weekdays */}
      <div style={styles.grid}>
        {["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].map((d) => (
          <div
            key={d}
            style={{
              ...styles.cell,
              fontWeight: "bold",
              background: "#f8f9fa",
              padding: "6px 0",
              textAlign: "center",
            }}
          >
            {d}
          </div>
        ))}
      </div>

      {/* Calendar */}
      <div style={styles.grid} onTouchStart={handleTouchStart} onTouchEnd={handleTouchEnd}>
        {calendarCells.map((cell, i) => {
          if (!cell) return <div key={i} style={styles.cell} />;

          const { date, noAvailability, isPast, isToday } = cell;

          const isStart = selectedRange.from === date;
          const isEnd = selectedRange.to === date;
          const inRange =
            selectedRange.from &&
            selectedRange.to &&
            dayjs(date).isBetween(dayjs(selectedRange.from), dayjs(selectedRange.to), null, "[]");

          let cardStyle = { ...styles.dayCard };
          if (isPast) cardStyle = { ...cardStyle, ...styles.pastDay };
          if (isToday) cardStyle = { ...cardStyle, ...styles.todayDay };
          if (inRange) cardStyle = { ...cardStyle, ...styles.dayCardSelected };
          if (isStart && !isEnd) cardStyle = { ...cardStyle, ...styles.checkInDate };
          if (isEnd && !isStart) cardStyle = { ...cardStyle, ...styles.checkOutDate };
          if (noAvailability) cardStyle = { ...cardStyle, opacity: 0.45, filter: "grayscale(100%)" };

          return (
            <div key={date} style={cardStyle} onClick={() => handleDayClick(date)}>
              <div style={styles.dateLabel}>{dayjs(date).date()}</div>
              {noAvailability && <div style={styles.noAvailabilitySlash} />}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default CalendarGrid;
